// =============================================================================
// Notification Dropdown Component
// src/app/shared/components/notification-dropdown/notification-dropdown.component.ts
// =============================================================================

import { Component, inject, signal, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { NOTIFICATION_TYPE_ICONS, NOTIFICATION_TYPE_COLORS, AppointmentNotificationResponse, AppointmentNotificationType } from '../../models/appointment-notification.model';
import { AppointmentNotificationService } from '../../services/appointment-notification.service';


@Component({
  selector: 'app-notification-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatRippleModule
  ],
  templateUrl: './notification-dropdown.component.html',
  styleUrl: './notification-dropdown.component.scss',
  encapsulation: ViewEncapsulation.None // Required for menu styling
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  // Services
  private readonly router = inject(Router);
  private readonly notificationService = inject(AppointmentNotificationService);

  // State from service
  readonly unreadCount = this.notificationService.unreadCount;
  readonly hasUnread = this.notificationService.hasUnread;
  readonly badgeText = this.notificationService.badgeText;
  readonly recentNotifications = this.notificationService.recentNotifications;
  readonly loading = this.notificationService.loading;

  // Local state
  readonly menuOpen = signal<boolean>(false);
  readonly markingAllRead = signal<boolean>(false);

  // Constants for template
  readonly notificationTypeIcons = NOTIFICATION_TYPE_ICONS;
  readonly notificationTypeColors = NOTIFICATION_TYPE_COLORS;

  // =============================================================================
  // LIFECYCLE HOOKS
  // =============================================================================

  ngOnInit(): void {
    // Start polling when component is initialized
    this.notificationService.startPolling(30000); // Poll every 30 seconds
  }

  ngOnDestroy(): void {
    // Stop polling when component is destroyed
    this.notificationService.stopPolling();
  }

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  /**
   * Handle menu opened event
   * تحديث الإشعارات عند فتح القائمة
   */
  onMenuOpened(): void {
    this.menuOpen.set(true);
    this.notificationService.refresh();
  }

  /**
   * Handle menu closed event
   */
  onMenuClosed(): void {
    this.menuOpen.set(false);
  }

  /**
   * Handle notification click - navigate to appointment details
   * عند النقر على الإشعار - الانتقال لصفحة تفاصيل الموعد
   */
  onNotificationClick(notification: AppointmentNotificationResponse): void {
    // Mark as read first
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.id).subscribe();
    }

    // Navigate to appointment details
    this.router.navigate(['/appointments', notification.appointmentId]);
  }

  /**
   * Handle "View All" click - navigate to notifications list page
   * عند النقر على "مشاهدة الكل" - الانتقال لصفحة قائمة الإشعارات
   */
  onViewAllClick(): void {
    this.router.navigate(['/notifications/appointments']);
  }

  /**
   * Mark all notifications as read
   * تحديد جميع الإشعارات كمقروءة
   */
  onMarkAllAsRead(event: Event): void {
    event.stopPropagation();

    this.markingAllRead.set(true);
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.markingAllRead.set(false);
      },
      error: () => {
        this.markingAllRead.set(false);
      }
    });
  }

  /**
   * Refresh notifications manually
   * تحديث الإشعارات يدوياً
   */
  onRefresh(event: Event): void {
    event.stopPropagation();
    this.notificationService.refresh();
  }

  // =============================================================================
  // HELPER METHODS
  // =============================================================================

  /**
   * Get icon for notification type
   */
  getNotificationIcon(type: AppointmentNotificationType): string {
    return this.notificationTypeIcons[type] || 'notifications';
  }

  /**
   * Get color for notification type
   */
  getNotificationColor(type: AppointmentNotificationType): string {
    return this.notificationTypeColors[type] || 'primary';
  }

  /**
   * Track function for ngFor optimization
   */
  trackByNotificationId(index: number, notification: AppointmentNotificationResponse): number {
    return notification.id;
  }
}
